import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem('groovlens_token');
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch (e) {
      localStorage.removeItem('groovlens_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (identifier, password) => {
    // NOTE: the API field is still called `email` for backward compatibility,
    // but it now accepts EITHER an email or a username. The backend detects
    // which by looking for `@`.
    const { data } = await api.post('/auth/login', { email: identifier, password });
    localStorage.setItem('groovlens_token', data.access_token);
    // Re-fetch /auth/me so the user object includes embedded subscription
    // status (login response is intentionally lean and doesn't include it).
    await fetchMe();
    return data.user;
  };

  const signup = async (email, password, name, username = null) => {
    const { data } = await api.post('/auth/signup', { email, password, name, username });
    localStorage.setItem('groovlens_token', data.access_token);
    await fetchMe();
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem('groovlens_token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, logout, refresh: fetchMe, refreshUser: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
